import { API_BASE_URL } from './baseUrl'

// Typed client for GET /api/stats/me (see backend/docs/auth/api.md). The server
// works streaks and stats out from the games it has recorded, so the app only
// ever *reads* them: nothing here can change a streak.

export interface StreakSummary {
  current: number
  longest: number
  playedToday: boolean
  /** A streak is running but today has not been played yet. */
  atRisk: boolean
  /** Streak freezes in the bank. */
  freezes: number
  /** A freeze covered yesterday, so the streak survived a missed day. */
  savedByFreeze: boolean
  nextMilestone: number | null
  lastPlayed: string | null
}

export interface StatsPayload {
  played: number
  won: number
  wordle: { played: number; won: number; guessDistribution: Record<string, number> }
  anagram: { played: number; won: number }
}

export interface StatsResult {
  /** The puzzle date (UTC) the streak was worked out for. */
  today: string
  streak: StreakSummary
  stats: StatsPayload
}

export type StatsApiErrorCode = 'STATS_UNAUTHORIZED' | 'STATS_RATE_LIMITED' | 'STATS_NETWORK' | 'STATS_UNKNOWN'

export class StatsApiError extends Error {
  readonly code: StatsApiErrorCode
  readonly status?: number

  constructor(code: StatsApiErrorCode, status?: number) {
    super(code)
    this.name = 'StatsApiError'
    this.code = code
    this.status = status
  }
}

const REQUEST_TIMEOUT_MS = 15_000

const isNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/** Rejects a response that is not shaped like a stats result, instead of showing garbage. */
export const isStatsResult = (value: unknown): value is StatsResult => {
  const v = value as Partial<StatsResult> | null
  return (
    !!v &&
    typeof v.today === 'string' &&
    !!v.streak &&
    isNumber(v.streak.current) &&
    isNumber(v.streak.longest) &&
    typeof v.streak.playedToday === 'boolean' &&
    typeof v.streak.atRisk === 'boolean' &&
    isNumber(v.streak.freezes) &&
    !!v.stats &&
    isNumber(v.stats.played) &&
    isNumber(v.stats.won) &&
    !!v.stats.wordle &&
    !!v.stats.anagram
  )
}

/**
 * The caller's streak and stats. Signed-in players pass their Firebase ID token
 * so every device on the account counts; guests pass only the device id.
 */
export const getMyStats = async (params: { deviceId: string; idToken?: string | null }): Promise<StatsResult> => {
  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}/api/stats/me`, {
      method: 'GET',
      headers: {
        'X-Device-Id': params.deviceId,
        ...(params.idToken && { Authorization: `Bearer ${params.idToken}` }),
      },
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    })
  } catch {
    throw new StatsApiError('STATS_NETWORK')
  }

  if (response.status === 401) throw new StatsApiError('STATS_UNAUTHORIZED', 401)
  if (response.status === 429) throw new StatsApiError('STATS_RATE_LIMITED', 429)

  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok || !isStatsResult(payload)) throw new StatsApiError('STATS_UNKNOWN', response.status)
  return payload
}
